import React, {Dispatch, FC, ReactNode, SetStateAction, useState} from 'react';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {CancelToken} from '../../../../../libs/shared/helpers/src/utilities/asyncCancelHelpers';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {AsyncSearchDropdown} from '../../../../components/common/dropdowns/search/asyncSearchDropdown';
import {RefinementOptionModel} from '../../../../models/api/refinementOptionModel';
import {RefinedInboxParameters} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {dropdownListDefaultRowHeight} from '../../../common/dropdowns/dropdownVirtualList';
import {ClearRefinementButtonVariantsEnum} from './clearRefinementButton';
import {ClearRefinementButtonContainer} from './clearRefinementButtonContainer';

export const REFINEMENT_SEARCH_DROPDOWN_TITLE = (
  <Trans id="cells-refinement-options-base-search-dropdown-title">Filter by</Trans>
);
export const REFINEMENT_SEARCH_DROPDOWN_WIDTH = 260;
export const REFINEMENT_SEARCH_DROPDOWN_HEIGHT = 342;
export const REFINEMENT_SEARCH_DROPDOWN_PAGINATION_LIMIT = 50;

export interface RefinementSearchDropdownPaginationState {
  query: string;
  offset: number;
}

type FetchData<T> = (
  paginationState: RefinementSearchDropdownPaginationState,
  data: ReadonlyArray<T>,
  query: string,
  cancelToken: CancelToken,
) => Promise<{
  data: ReadonlyArray<T>;
  canRequestMore: boolean;
}>;

/*
 * Props.
 */

interface CellsRefinementOptionsBaseSearchDropdownProps<T> {
  popoverProps: PopoverTransferProps;
  selectedRefinement: RefinementOptionModel | undefined;
  parameters: RefinedInboxParameters;
  renderRow: (item: T, index: number, data: ReadonlyArray<T>, searchQuery: string) => ReactNode;
  fetchData: FetchData<T>;
  onDropdownNavigateBack: Handler | undefined;
}

/*
 * Component.
 */

export const CellsRefinementOptionsBaseSearchDropdown = <T,>(
  props: CellsRefinementOptionsBaseSearchDropdownProps<T>,
): ReturnType<FC<CellsRefinementOptionsBaseSearchDropdownProps<T>>> => {
  const {popoverProps, renderRow, fetchData, onDropdownNavigateBack, selectedRefinement, parameters} = props;

  const [paginationState, setPaginationState] = useState({
    query: '',
    offset: 0,
  });

  return (
    <AsyncSearchDropdown<T>
      title={REFINEMENT_SEARCH_DROPDOWN_TITLE}
      width={REFINEMENT_SEARCH_DROPDOWN_WIDTH}
      height={REFINEMENT_SEARCH_DROPDOWN_HEIGHT}
      findRowHeight={() => dropdownListDefaultRowHeight}
      renderRow={(...args) => renderRow(...args, paginationState.query)}
      backButtonLabel={<Trans id="cells-refinement-options-base-dropdown-navigate-back">Back</Trans>}
      rightIcon={renderRefinementSearchDropdownHeaderRight(popoverProps, selectedRefinement, parameters)}
      onBackButtonClick={onDropdownNavigateBack}
      onRequestData={(...args) => onRequestData(paginationState, setPaginationState, fetchData, ...args)}
      onRequestClose={popoverProps.onRequestClose}
      anchor={popoverProps.anchor}
      shouldIncludeSearchIcon
    />
  );
};

async function onRequestData<T>(
  paginationState: RefinementSearchDropdownPaginationState,
  setPaginationState: Dispatch<SetStateAction<RefinementSearchDropdownPaginationState>>,
  fetchData: FetchData<T>,
  query: string,
  data: ReadonlyArray<T>,
  cancelToken: CancelToken,
) {
  const newPaginationState = query === paginationState.query ? paginationState : {offset: 0, query};

  const newData = fetchData(newPaginationState, data, query, cancelToken);

  setPaginationState({
    ...newPaginationState,
    offset: newPaginationState.offset + REFINEMENT_SEARCH_DROPDOWN_PAGINATION_LIMIT,
  });

  return newData;
}

export function renderRefinementSearchDropdownHeaderRight(
  popoverProps: PopoverTransferProps,
  selectedRefinement: RefinementOptionModel | undefined,
  parameters: RefinedInboxParameters,
) {
  if (selectedRefinement === undefined) {
    return null;
  }

  return (
    <ClearRefinementButtonContainer
      onClear={popoverProps.onRequestClose}
      variant={ClearRefinementButtonVariantsEnum.LINK}
      parameters={parameters}
    />
  );
}
